import React, { useMemo, useState } from 'react';
import { Info, Picture, Delete as DeleteIcon, Loading } from '@icon-park/react';
import { uploadFiles, FILE_TYPES } from '../../api/upload';
import { compressImages } from '../../utils/fileUtils';
import { IMAGE_COMPRESS_CONFIG } from '../../config/upload.config';
import './styles/ImageUploader.css';

function ImageUploader({ data, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const carousel = data.carousel || {};
  const intervalOptions = useMemo(() => [
    { label: '2 秒', value: 2000 },
    { label: '3 秒', value: 3000 },
    { label: '5 秒', value: 5000 },
    { label: '8 秒', value: 8000 },
    { label: '10 秒', value: 10000 },
  ], []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      // 1. 压缩图片（可选，提高上传速度）
      let filesToUpload = files;
      if (IMAGE_COMPRESS_CONFIG.enabled) {
        console.log('正在压缩图片...');
        filesToUpload = await compressImages(files);
        console.log('图片压缩完成');
      }

      // 2. 上传到OSS/CDN
      console.log('正在上传图片到云存储...');
      const uploadedUrls = await uploadFiles(filesToUpload, {
        fileType: FILE_TYPES.IMAGE,
        folder: 'labels/images',
        onProgress: (percent) => {
          setUploadProgress(percent);
        },
        onEachProgress: (index, percent) => {
          console.log(`图片 ${index + 1}/${files.length} 上传进度: ${percent}%`);
        }
      });

      // 3. 更新数据（只保存URL，不保存Base64）
      const newImages = uploadedUrls.map(url => ({
        src: url,  // ✅ CDN URL
        width: 100  // 默认100%宽度
      }));

      const existingImages = (data.images || []).map(img => 
        typeof img === 'string' ? { src: img, width: 100 } : img
      );

      onUpdate({ ...data, images: [...existingImages, ...newImages] });
      
      console.log('图片上传成功:', uploadedUrls);
    } catch (err) {
      console.error('图片上传失败:', err);
      setError(err.message || '图片上传失败，请重试');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = (index) => {
    const newImages = data.images.filter((_, i) => i !== index);
    onUpdate({ ...data, images: newImages });
  };

  const handleImageWidthChange = (index, width) => {
    const newImages = [...data.images];
    if (typeof newImages[index] === 'string') {
      newImages[index] = { src: newImages[index], width: parseInt(width) };
    } else {
      newImages[index] = { ...newImages[index], width: parseInt(width) };
    }
    onUpdate({ ...data, images: newImages });
  };

  const handleBackgroundColorChange = (color) => {
    onUpdate({ ...data, backgroundColor: color });
  };

  const handleDescriptionChange = (e) => {
    onUpdate({ ...data, description: e.target.value });
  };

  const handleCarouselChange = (patch) => {
    onUpdate({
      ...data,
      carousel: {
        enabled: false,
        autoplay: true,
        interval: 3000,
        showArrows: true,
        showDots: true,
        loop: true,
        ...carousel,
        ...patch
      }
    });
  };

  return (
    <div className="image-uploader-container">
      {/* 背景颜色选择区域 - 视觉优化 */}
      <div className="config-section">
        <div className="section-header">
          <span className="section-title-primary">选择背景颜色</span>
          <span className="info-icon-wrapper" title="设置图片区域的背景颜色">
            <Info theme="outline" size={16} fill="#64748b" strokeWidth={3} />
          </span>
        </div>
        
        <div className="color-select-area-enhanced">
          <div className="color-picker-wrapper">
            <input
              type="color"
              value={data.backgroundColor || '#ffffff'}
              onChange={(e) => handleBackgroundColorChange(e.target.value)}
              className="color-input-enhanced"
              id="bg-color-picker"
            />
            <label htmlFor="bg-color-picker" className="color-picker-label">
              <div 
                className="color-preview-box" 
                style={{ backgroundColor: data.backgroundColor || '#ffffff' }}
              />
            </label>
          </div>
          <div className="color-value-wrapper">
            <span className="color-value-enhanced">{data.backgroundColor || '#ffffff'}</span>
          </div>
        </div>
      </div>
      
      {/* 图片上传区域 - 视觉吸引力提升 */}
      <div className="config-section">
        <div className="section-header">
          <h3 className="section-title-primary">图片添加</h3>
        </div>
        
        {/* 错误提示 */}
        {error && (
          <div className="upload-error-enhanced">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
          </div>
        )}

        <div className="upload-area-enhanced">
          <input
            type="file"
            id="image-upload"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          <label 
            htmlFor="image-upload" 
            className={`upload-box-enhanced ${uploading ? 'uploading' : ''}`}
          >
            {uploading ? (
              <div className="upload-state-uploading">
                <div className="upload-icon-wrapper">
                  <Loading theme="outline" size={32} fill="#2563EB" strokeWidth={3} />
                </div>
                <div className="upload-status-text">上传中...</div>
                <div className="upload-progress-wrapper">
                  <div className="upload-progress-bar-enhanced">
                    <div 
                      className="upload-progress-fill-enhanced" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              </div>
            ) : (
              <div className="upload-state-ready">
                <div className="upload-icon-wrapper">
                  <Picture theme="outline" size={48} fill="#2563EB" strokeWidth={3} />
                </div>
                <div className="upload-primary-text">点击上传图片</div>
                <div className="upload-hint-enhanced">支持 JPG、PNG、GIF，最大 5MB</div>
              </div>
            )}
          </label>
        </div>

        {/* 图片预览网格 */}
        {data.images && data.images.length > 0 && (
          <div className="image-preview-grid-enhanced">
            {data.images.map((img, index) => {
              const imgSrc = typeof img === 'string' ? img : img.src;
              const imgWidth = typeof img === 'string' ? 100 : (img.width || 100);
              
              return (
                <div key={index} className="preview-image-item-enhanced">
                  <div className="preview-image-wrapper">
                    <img src={imgSrc} alt={`预览 ${index + 1}`} />
                    <button 
                      className="delete-image-btn-enhanced"
                      onClick={() => handleDeleteImage(index)}
                      title="删除图片"
                    >
                      <DeleteIcon theme="filled" size={16} fill="#ffffff" strokeWidth={3} />
                    </button>
                  </div>
                  <div className="image-width-control-enhanced">
                    <div className="width-label-enhanced">
                      <span className="width-label-text">宽度</span>
                      <span className="width-value-text">{imgWidth}%</span>
                    </div>
                    <input
                      type="range"
                      min="20"
                      max="100"
                      value={imgWidth}
                      onChange={(e) => handleImageWidthChange(index, e.target.value)}
                      className="width-slider-enhanced"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 文字描述区域 */}
      <div className="config-section">
        <div className="section-header">
          <label className="section-title-primary">文字描述</label>
        </div>
        <div className="textarea-wrapper">
          <textarea
            className="description-input-enhanced"
            placeholder="请输入图片描述..."
            value={data.description || ''}
            onChange={handleDescriptionChange}
            rows={4}
          />
          <div className="textarea-footer">
            <span className="char-count">{data.description?.length || 0} 字</span>
          </div>
        </div>
      </div>

      {/* 图片样式控制区域 */}
      <div className="config-section">
        <div className="section-header">
          <label className="section-title-primary">图片样式</label>
        </div>
        <div className="image-style-controls">
          <div className="style-control-group">
            <label className="style-control-label">圆角</label>
            <div className="style-control-input-wrapper">
              <input 
                type="number" 
                min="0" 
                max="48" 
                value={(data.imageStyle && data.imageStyle.radius) || 0} 
                onChange={(e) => onUpdate({ ...data, imageStyle: { ...(data.imageStyle || {}), radius: Number(e.target.value) } })} 
                className="style-control-input"
              />
              <span className="style-control-unit">px</span>
            </div>
          </div>
          
          <div className="style-control-group">
            <label className="style-control-label">边框宽</label>
            <div className="style-control-input-wrapper">
              <input 
                type="number" 
                min="0" 
                max="12" 
                value={(data.imageStyle && data.imageStyle.borderWidth) || 0} 
                onChange={(e) => onUpdate({ ...data, imageStyle: { ...(data.imageStyle || {}), borderWidth: Number(e.target.value) } })} 
                className="style-control-input"
              />
              <span className="style-control-unit">px</span>
            </div>
          </div>
          
          <div className="style-control-group">
            <label className="style-control-label">边框色</label>
            <div className="style-control-color-wrapper">
              <input 
                type="color" 
                value={(data.imageStyle && data.imageStyle.borderColor) || '#e8eaed'} 
                onChange={(e) => onUpdate({ ...data, imageStyle: { ...(data.imageStyle || {}), borderColor: e.target.value } })} 
                className="style-control-color-input"
                id="border-color-picker"
              />
              <label 
                htmlFor="border-color-picker"
                className="style-control-color-preview"
                style={{ backgroundColor: (data.imageStyle && data.imageStyle.borderColor) || '#e8eaed' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 轮播设置 */}
      <div className="config-section">
        <div className="section-header">
          <label className="section-title-primary">轮播设置</label>
        </div>
        <div className="carousel-config-grid">
          <div className="carousel-config-row">
            <label className="style-control-label">启用轮播</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={!!carousel.enabled}
                onChange={(e) => handleCarouselChange({ enabled: e.target.checked })}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="carousel-config-row">
            <label className="style-control-label">自动播放</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={carousel.autoplay !== false}
                onChange={(e) => handleCarouselChange({ autoplay: e.target.checked })}
                disabled={!carousel.enabled}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="carousel-config-row">
            <label className="style-control-label">循环播放</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={carousel.loop !== false}
                onChange={(e) => handleCarouselChange({ loop: e.target.checked })}
                disabled={!carousel.enabled}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="carousel-config-row">
            <label className="style-control-label">显示切换箭头</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={carousel.showArrows !== false}
                onChange={(e) => handleCarouselChange({ showArrows: e.target.checked })}
                disabled={!carousel.enabled}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="carousel-config-row">
            <label className="style-control-label">显示指示点</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={carousel.showDots !== false}
                onChange={(e) => handleCarouselChange({ showDots: e.target.checked })}
                disabled={!carousel.enabled}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="carousel-config-row">
            <label className="style-control-label">自动播放间隔</label>
            <select
              className="carousel-select"
              value={carousel.interval || 3000}
              onChange={(e) => handleCarouselChange({ interval: Number(e.target.value) })}
              disabled={!carousel.enabled}
            >
              {intervalOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageUploader;
